import React, { useState, useMemo, useRef, useEffect } from "react";
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';

const { FiChevronUp, FiChevronDown, FiSearch, FiFilter } = FiIcons;

// ============ static config ============
const AMCS = ["ICICI Prudential", "Nippon India", "Motilal Oswal", "Parag Parikh", "Invesco India", "Edelweiss", "Aditya Birla", "Mirae Asset", "Canara Robeco", "SBI", "HDFC", "Axis", "Kotak", "UTI", "DSP", "Tata", "Franklin", "Quant", "Sundaram", "Bandhan", "PGIM", "Baroda", "Union", "JM"];

const PARAM_DEFS = [
  { key: "amc", label: "AMC concentration risk", max: 20 },
  { key: "cat", label: "Category duplication", max: 20 },
  { key: "chase", label: "Return chasing (1–2 yr)", max: 10 },
  { key: "track", label: "Track record / asset base", max: 10 },
  { key: "sector", label: "Sector & thematic overload", max: 10 },
  { key: "sell", label: "Sell discipline / exit timing", max: 20 },
  { key: "alloc", label: "Allocation concentration", max: 10 },
];

const VMETA = {
  buy: { label: "BUY", color: "#34E2A0", bg: "rgba(52,226,160,.12)", border: "rgba(52,226,160,.34)" },
  hold: { label: "HOLD", color: "#5B9DFF", bg: "rgba(91,157,255,.12)", border: "rgba(91,157,255,.32)" },
  sellsoon: { label: "SELL · 1–3Y", color: "#FFB13C", bg: "rgba(255,177,60,.13)", border: "rgba(255,177,60,.34)" },
  sell: { label: "SELL", color: "#FF5C72", bg: "rgba(255,92,114,.13)", border: "rgba(255,92,114,.34)" },
};

// ============ pure helpers ============
const safeNum = (val) => {
  if (typeof val === 'number') return val;
  if (!val) return 0;
  return parseFloat(String(val).replace(/[₹,%\s]/gi, '').trim()) || 0;
};

const amcOf = (name) => {
  if (!name) return "—";
  const m = AMCS.find((a) => name.indexOf(a) === 0 || name.indexOf(a) > -1);
  return m || name.split(" ")[0] || "—";
};

const isSector = (cat) => /sector|thematic|pharma|tech|bank|financ|infra|digital|energy|consum|health|psu|manufactur|silver|gold|commodity/i.test(cat || "");

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const inr = (n) => {
  const s = Math.round(n).toString();
  if (s.length <= 3) return s;
  const l = s.slice(-3);
  const r = s.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ",");
  return r + "," + l;
};

const parsePastedData = (text) => {
  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(l => l !== "");
  const parsed = [];
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].startsWith('₹')) {
      const cv = safeNum(lines[i]);
      if (isNaN(cv) || cv === 0) continue;
      let name = "";
      let cat = "";
      let nameIdx = i - 2;
      let catIdx = i - 1;
      if (i >= 3 && (lines[i-1].toLowerCase().includes('rated') || lines[i-1].length < 3 || /^[1-5](\.0)?$/.test(lines[i-1]))) {
        catIdx = i - 2;
        nameIdx = i - 3;
      }
      name = lines[nameIdx] || "Unknown Fund";
      cat = lines[catIdx] || "Direct";
      const xirr = safeNum(lines[i+1]);
      const exp = safeNum(lines[i+2]);
      const r1 = safeNum(lines[i+3]);
      const nav = safeNum(lines[i+4]);
      const aum = (lines[i+5] || "").replace(/Cr/gi, '').trim() || "0";
      parsed.push({ id: Math.random().toString(36).substr(2, 9), name, cat, cv, xirr, exp, r1, nav, aum });
      i += 5;
    }
  }
  return parsed;
};

function scoreOf(fundsList) {
  const total = fundsList.reduce((a, f) => a + safeNum(f.cv), 0) || 1;
  const amcW = {}, catW = {}, catN = {};
  fundsList.forEach((f) => {
    const a = amcOf(f.name);
    const cv = safeNum(f.cv);
    amcW[a] = (amcW[a] || 0) + (cv / total) * 100;
    catW[f.cat] = (catW[f.cat] || 0) + (cv / total) * 100;
    catN[f.cat] = (catN[f.cat] || 0) + 1;
  });
  const amcEntries = Object.entries(amcW).sort((a, b) => b[1] - a[1]);
  const maxAmc = amcEntries.length ? amcEntries[0][1] : 0;
  const distinct = Object.keys(catN).length;
  const dupExtra = Math.max(0, fundsList.length - distinct);
  const chase = fundsList.filter((f) => safeNum(f.r1) < -5).length;
  const thin = fundsList.filter((f) => safeNum(f.aum) < 500).length;
  const sector = fundsList.filter((f) => isSector(f.cat)).length;
  const lag = fundsList.filter((f) => safeNum(f.r1) < -3 || (safeNum(f.xirr) < 6 && safeNum(f.r1) < 0)).length;
  const maxCat = Object.values(catW).length ? Math.max(...Object.values(catW)) : 0;
  const r = (v, lo, hi) => Math.round(clamp(v, lo, hi));
  const byKey = {
    amc: { earned: r(20 - Math.max(0, maxAmc - 15) * 1.2, 4, 20), hint: `Largest AMC at ${maxAmc.toFixed(0)}%.` },
    cat: { earned: r(20 - dupExtra * 3, 4, 20), hint: dupExtra > 0 ? `${dupExtra} duplicate categories.` : "No duplication." },
    chase: { earned: r(10 - chase * 3, 2, 10), hint: chase > 0 ? `${chase} fund down >5% over 1 yr.` : "Stable returns." },
    track: { earned: r(10 - thin * 4, 2, 10), hint: thin > 0 ? `${thin} fund with thin assets.` : "Solid asset base." },
    sector: { earned: r(10 - sector * 3, 1, 10), hint: sector > 0 ? `${sector} thematic fund(s).` : "No thematic bets." },
    sell: { earned: r(20 - lag * 6, 4, 20), hint: lag > 0 ? `${lag} laggard(s) found.` : "No laggards." },
    alloc: { earned: r(10 - Math.max(0, maxCat - 30) * 0.6, 2, 10), hint: `Top category at ${maxCat.toFixed(0)}%.` },
  };
  const totalScore = PARAM_DEFS.reduce((a, d) => a + byKey[d.key].earned, 0);
  return { total: totalScore, byKey };
}

function optimizedFunds(verdicts, currentFunds) {
  const kept = currentFunds.filter((f) => verdicts[f.id] === "buy" || verdicts[f.id] === "hold");
  if (!kept.length) return [];
  const total = currentFunds.reduce((a, f) => a + safeNum(f.cv), 0);
  let units = kept.map((f) => (verdicts[f.id] === "buy" ? 1.4 : 1.0));
  let uSum = units.reduce((a, b) => a + b, 0);
  let w = units.map((u) => (u / uSum) * 100);
  for (let pass = 0; pass < 3; pass++) {
    let excess = 0;
    const under = [];
    w = w.map((x, i) => {
      if (x > 15) { excess += x - 15; return 15; }
      under.push(i);
      return x;
    });
    if (excess < 0.01 || !under.length) break;
    const uw = under.reduce((a, i) => a + w[i], 0) || 1;
    under.forEach((i) => { w[i] += excess * (w[i] / uw); });
  }
  return kept.map((f, i) => ({ ...f, cv: (total * w[i]) / 100 }));
}

export default function PortfolioReview() {
  const [funds, setFunds] = useState([]);
  const [phase, setPhase] = useState("select");
  const [displayScore, setDisplayScore] = useState(0);
  const [verdicts, setVerdicts] = useState({});
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [rawPaste, setRawPaste] = useState("");
  
  const [searchTerm, setSearchTerm] = useState("");
  const [catFilter, setCatFilter] = useState("All");
  const [callFilter, setCallFilter] = useState("All");
  const [sortConfig, setSortConfig] = useState({ key: 'cv', direction: 'desc' });

  const rafRef = useRef(null);

  useEffect(() => {
    const newVerdicts = { ...verdicts };
    let changed = false;
    funds.forEach(f => {
      if (!newVerdicts[f.id]) {
        const r1 = safeNum(f.r1);
        const xirr = safeNum(f.xirr);
        const exp = safeNum(f.exp);
        if (r1 < -3) newVerdicts[f.id] = "sell";
        else if (xirr >= 18 && exp <= 0.6) newVerdicts[f.id] = "buy";
        else if (xirr < 6 && r1 < 0) newVerdicts[f.id] = "sellsoon";
        else newVerdicts[f.id] = "hold";
        changed = true;
      }
    });
    if (changed) setVerdicts(newVerdicts);
  }, [funds, verdicts]);

  const setVerdict = (id, val) => setVerdicts((prev) => ({ ...prev, [id]: val }));
  const updateFund = (id, field, val) => setFunds(prev => prev.map(f => f.id === id ? { ...f, [field]: val } : f));
  const removeFund = (id) => setFunds(prev => prev.filter(f => f.id !== id));

  const handleLoadData = () => {
    const parsed = parsePastedData(rawPaste);
    if (parsed.length > 0) {
      setFunds(prev => [...prev, ...parsed]);
      setPhase("select");
      setDisplayScore(0);
      setRawPaste("");
    } else {
      alert("No valid data found.");
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const clearAll = () => {
    setFunds([]); setVerdicts({}); setPhase("select"); setDisplayScore(0);
    setRawPaste(""); setClientName(""); setClientEmail("");
    setSearchTerm(""); setCatFilter("All"); setCallFilter("All");
    setSortConfig({ key: 'cv', direction: 'desc' });
  };

  const optimize = () => {
    if (phase !== "select") return;
    setPhase("optimizing");
    const target = scoreOf(funds).total;
    const t0 = performance.now(), dur = 1500;
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / dur);
      const e = 1 - Math.pow(1 - p, 3);
      setDisplayScore(Math.round(target * e));
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
      else { setPhase("done"); setDisplayScore(target); }
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const reset = () => { setPhase("select"); setDisplayScore(0); };
  const onDownload = () => window.print();

  const v = useMemo(() => {
    const optimizing = phase === "optimizing", done = phase === "done";
    const total = funds.reduce((a, f) => a + safeNum(f.cv), 0) || 1;
    let wExp = 0, w1 = 0, wX = 0;
    
    const cnt = { buy: 0, hold: 0, sell: 0, sellsoon: 0 };
    funds.forEach((f) => { const vd = verdicts[f.id] || 'hold'; if(cnt[vd] !== undefined) cnt[vd]++; });

    const allRows = funds.map((f, i) => {
      const cvNum = safeNum(f.cv);
      const w = cvNum / total;
      wExp += w * safeNum(f.exp); w1 += w * safeNum(f.r1); wX += w * safeNum(f.xirr);
      const vm = VMETA[verdicts[f.id]] || VMETA.hold;
      return { 
        ...f, 
        allocNum: w * 100,
        allocStr: (w * 100).toFixed(2) + "%", 
        r1Color: safeNum(f.r1) > 0 ? "#34E2A0" : safeNum(f.r1) < 0 ? "#FF5C72" : "#A9B6CC",
        verdict: verdicts[f.id] || 'hold', 
        vm
      };
    });

    const categories = ["All", ...new Set(funds.map(f => f.cat))].sort();

    let processed = allRows.filter(r => {
      const matchSearch = r.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchCat = catFilter === "All" || r.cat === catFilter;
      const matchCall = callFilter === "All" || r.verdict === callFilter;
      return matchSearch && matchCat && matchCall;
    });

    processed.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      if (['cv', 'xirr', 'r1', 'exp', 'allocNum'].includes(sortConfig.key)) {
        aVal = safeNum(aVal); bVal = safeNum(bVal);
      } else {
        aVal = String(aVal).toLowerCase(); bVal = String(bVal).toLowerCase();
      }
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    const sBefore = scoreOf(funds);
    const optFunds = optimizedFunds(verdicts, funds);
    const sAfter = optFunds.length ? scoreOf(optFunds) : sBefore;

    // Projected Metrics
    let o1 = 0, oX = 0, oExp = 0;
    const oTot = optFunds.reduce((a, f) => a + safeNum(f.cv), 0) || 1;
    optFunds.forEach((f) => { 
      const w = safeNum(f.cv) / oTot; 
      o1 += w * safeNum(f.r1); oX += w * safeNum(f.xirr); oExp += w * safeNum(f.exp);
    });
    
    return {
      count: funds.length, totalValue: inr(total),
      wExp: wExp.toFixed(2) + "%", w1yr: (w1 > 0 ? "+" : "") + w1.toFixed(2) + "%", w1yrColor: w1 > 0 ? "#34E2A0" : w1 < 0 ? "#FF5C72" : "#EAF0F8", wXirr: wX.toFixed(2) + "%",
      rows: processed, categories,
      optimizing, done, ring: `conic-gradient(${displayScore >= 80 ? "#34E2A0" : displayScore >= 65 ? "#FFB13C" : "#FF8A5C"} ${displayScore * 3.6}deg, rgba(255,255,255,.07) 0deg)`,
      scoreColor: displayScore >= 80 ? "#34E2A0" : displayScore >= 65 ? "#FFB13C" : "#FF8A5C",
      verdict: sBefore.total >= 80 ? "Strong" : sBefore.total >= 68 ? "Fair" : sBefore.total >= 55 ? "Attention" : "High risk",
      subs: PARAM_DEFS.map(d => {
        const b = sBefore.byKey[d.key];
        const rt = b.earned / d.max;
        return { ...d, valStr: b.earned + " / " + d.max, w: rt * 100 + "%", color: rt >= 0.85 ? "#34E2A0" : rt >= 0.6 ? "#7BD88F" : rt >= 0.4 ? "#FFB13C" : "#FF8A5C" };
      }),
      afterScore: sAfter.total, scoreUplift: Math.max(0, sAfter.total - sBefore.total),
      counts: { buy: cnt.buy, hold: cnt.hold, sell: cnt.sell + cnt.sellsoon, total: funds.length },
      deltas: [
        { label: "Weighted 1-yr return", to: (o1 > 0 ? "+" : "") + o1.toFixed(2) + "%" },
        { label: "Weighted XIRR", to: oX.toFixed(2) + "%" },
        { label: "Weighted expense", to: oExp.toFixed(2) + "%" }
      ],
      ctaLabel: optimizing ? "Analyzing…" : done ? "↺ Re-run" : "⚡ Optimize",
      ctaDisabled: optimizing || funds.length === 0,
    };
  }, [phase, displayScore, verdicts, funds, searchTerm, catFilter, callFilter, sortConfig]);

  const ctaOnClick = v.done ? reset : optimize;

  const SortHeader = ({ label, sortKey, align = "left" }) => {
    const isActive = sortConfig.key === sortKey;
    return (
      <div onClick={() => handleSort(sortKey)} style={{ padding: "12px 8px", font: "800 10px Manrope", color: isActive ? "#34E2A0" : "#8794AB", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: align === "right" ? "flex-end" : "center", gap: 4 }}>
        {label}
        <div className="no-print" style={{ display: "flex", flexDirection: "column", fontSize: 8 }}>
          <SafeIcon icon={FiChevronUp} style={{ opacity: isActive && sortConfig.direction === 'asc' ? 1 : 0.3, marginBottom: -2 }} />
          <SafeIcon icon={FiChevronDown} style={{ opacity: isActive && sortConfig.direction === 'desc' ? 1 : 0.3 }} />
        </div>
      </div>
    );
  };

  return (
    <div className="portfolio-wrapper">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Space+Grotesk:wght@400;500;600;700&display=swap');
        .portfolio-wrapper { width: 100%; min-height: 100vh; background: #0A0D14; font-family: 'Manrope', sans-serif; color: #F2F5FA; padding-bottom: 56px; }
        .container { max-width: 1360px; margin: 0 auto; padding: 0 16px; }
        .score-panel { background: rgba(255,255,255,.03); border: 1px solid rgba(255,255,255,0.08); border-radius: 20px; padding: 24px; display: flex; flex-direction: column; gap: 32px; margin-bottom: 24px; }
        @media (min-width: 1024px) { .score-panel { flex-direction: row; } }
        .uplift-section { flex: 0 0 auto; min-width: 320px; border-top: 1px solid rgba(255,255,255,.07); padding-top: 24px; }
        @media (min-width: 1024px) { .uplift-section { border-top: none; border-left: 1px solid rgba(255,255,255,.07); padding-left: 32px; } }
        .top-nav { position: sticky; top: 0; z-index: 40; border-bottom: 1px solid rgba(255,255,255,.06); background: rgba(10,13,20,.85); backdrop-filter: blur(14px); }
        .top-nav-inner { max-width: 1360px; margin: 0 auto; padding: 12px 16px; display: flex; align-items: center; justify-content: space-between; }
        .editable-cell { background: transparent; border: 1px solid transparent; outline: none; color: inherit; font: inherit; width: 100%; border-radius: 6px; padding: 4px; transition: all 0.2s; }
        .editable-cell:hover, .editable-cell:focus { border-color: rgba(255,255,255,0.15); background: rgba(255,255,255,0.03); }
        .print-only { display: none !important; }
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          body { background: #0A0D14 !important; color: #F2F5FA !important; }
          .portfolio-wrapper { background: #0A0D14 !important; }
          .container { max-width: 100% !important; padding: 0 !important; }
          .editable-cell, .filter-input, .snap-sel { display: none !important; }
          .score-panel { break-inside: avoid; border: 1px solid rgba(255,255,255,0.1) !important; flex-direction: row !important; }
          .snap-scroll { border: 1px solid rgba(255,255,255,0.1) !important; overflow: visible !important; }
          .uplift-section { border-left: 1px solid rgba(255,255,255,.07) !important; padding-left: 32px !important; border-top: none !important; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      `}</style>

      <nav className="top-nav no-print">
        <div className="top-nav-inner">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img src="https://media-manager-c.questera.ai/greta-media/5f94a250bead307152c531a49a2765ac68b9f09115a37a48f4e871b76de3a9bd97e4f5dec341b9ee20c4744fb0049fa1/images/aW1hZ2UvcG5n/c0b5caa060dca4e2eabc014e96c494a5.png" alt="Logo" style={{ height: "32px" }} />
            <div style={{ borderLeft: "1px solid rgba(255,255,255,.15)", paddingLeft: 12 }}>
              <div style={{ font: "600 12px Manrope" }}>Portfolio Review</div>
              <div style={{ font: "500 10px Manrope", color: "#8794AB" }}>INH000010858</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={clearAll} style={{ padding: "10px 14px", borderRadius: 10, cursor: "pointer", font: "700 12px Manrope", color: "#FF5C72", background: "rgba(255,92,114,.08)", border: "1px solid rgba(255,92,114,.15)" }}>Clear</button>
            <button onClick={onDownload} style={{ padding: "10px 14px", borderRadius: 10, cursor: "pointer", font: "700 12px Manrope", color: "#EAF0F8", background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.12)" }}>Download</button>
            <button onClick={ctaOnClick} disabled={v.ctaDisabled} style={{ padding: "10px 16px", borderRadius: 10, border: "none", cursor: "pointer", font: "700 12px Manrope", color: "#06140D", background: "linear-gradient(135deg,#34E2A0,#15B886)", opacity: v.ctaDisabled ? 0.6 : 1 }}>{v.ctaLabel}</button>
          </div>
        </div>
      </nav>

      <div className="container">
        <header style={{ padding: "24px 0 18px" }}>
          <div style={{ font: "600 11px Manrope", color: "#8794AB", textTransform: "uppercase" }}>Analysis · {v.count} funds</div>
          <h1 style={{ font: "800 24px 'Space Grotesk'" }}> {clientName ? `${clientName}'s ` : ""}Holdings & Optimization</h1>
          {clientEmail && <div className="print-only" style={{ font: "500 12px Manrope", color: "#8794AB", marginTop: 4 }}>{clientEmail}</div>}
        </header>

        <div className="no-print" style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 24 }}>
          <div style={{ flex: "1 1 300px", padding: "16px", background: "rgba(255,255,255,.03)", border: "1px solid rgba(255,255,255,.08)", borderRadius: 16 }}>
            <div style={{ font: "700 11px Manrope", color: "#8794AB", marginBottom: 12 }}>CLIENT INFO</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <input value={clientName} onChange={(e)=>setClientName(e.target.value)} placeholder="Name" className="filter-input" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: 8, color: "#FFF", fontSize: 12 }} />
              <input value={clientEmail} onChange={(e)=>setClientEmail(e.target.value)} placeholder="Email" className="filter-input" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: 8, color: "#FFF", fontSize: 12 }} />
            </div>
          </div>
          <div style={{ flex: "2 1 400px", padding: "16px", background: "rgba(255,255,255,.02)", border: "1px dashed rgba(52,226,160,.2)", borderRadius: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ font: "700 11px Manrope", color: "#34E2A0" }}>PASTE DATA</span>
              <button onClick={handleLoadData} style={{ padding: "4px 12px", borderRadius: 6, background: "#34E2A0", color: "#000", fontWeight: 700, fontSize: 10 }}>PARSE</button>
            </div>
            <textarea value={rawPaste} onChange={(e)=>setRawPaste(e.target.value)} placeholder="Paste portfolio data here..." style={{ width: "100%", height: "42px", background: "transparent", color: "#FFF", border: "none", outline: "none", resize: "none" }} />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 24 }}>
          <div style={{ background: "rgba(52,226,160,.05)", border: "1px solid rgba(52,226,160,.15)", borderRadius: 16, padding: "16px" }}>
            <div style={{ font: "600 11px Manrope", color: "#34E2A0" }}>Total Value</div>
            <div style={{ font: "700 22px 'Space Grotesk'", color: "#34E2A0" }}>₹{v.totalValue}</div>
          </div>
          <div style={{ background: "rgba(255,255,255,.02)", border: "1px solid rgba(255,255,255,.08)", borderRadius: 16, padding: "16px" }}>
            <div style={{ font: "600 11px Manrope", color: "#8794AB" }}>1-yr Return</div>
            <div style={{ font: "700 22px 'Space Grotesk'", color: v.w1yrColor }}>{v.w1yr}</div>
          </div>
          <div style={{ background: "rgba(255,255,255,.02)", border: "1px solid rgba(255,255,255,.08)", borderRadius: 16, padding: "16px" }}>
            <div style={{ font: "600 11px Manrope", color: "#8794AB" }}>Avg Expense</div>
            <div style={{ font: "700 22px 'Space Grotesk'" }}>{v.wExp}</div>
          </div>
          <div style={{ background: "rgba(255,255,255,.02)", border: "1px solid rgba(255,255,255,.08)", borderRadius: 16, padding: "16px" }}>
            <div style={{ font: "600 11px Manrope", color: "#8794AB" }}>Avg XIRR</div>
            <div style={{ font: "700 22px 'Space Grotesk'" }}>{v.wXirr}</div>
          </div>
        </div>

        {v.done && (
          <div className="score-panel">
            <div style={{ flex: "0 0 160px", display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div style={{ position: "relative", width: 140, height: 140 }}>
                <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: v.ring }} />
                <div style={{ position: "absolute", inset: 10, borderRadius: "50%", background: "#0C121C", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ font: "700 40px 'Space Grotesk'", color: v.scoreColor }}>{displayScore}</span>
                  <span style={{ font: "600 10px Manrope", color: "#5E6B82" }}>/ 100</span>
                </div>
              </div>
              <div style={{ textAlign: "center", marginTop: 12 }}>
                <div style={{ font: "700 14px Manrope", color: v.scoreColor }}>{v.verdict}</div>
                <div style={{ font: "600 10px Manrope", color: "#5E6B82", textTransform: "uppercase" }}>Health Score</div>
              </div>
            </div>
            
            <div style={{ flex: 1 }}>
              <div style={{ font: "800 11px Manrope", color: "#9AA7BD", textTransform: "uppercase", marginBottom: 16 }}>Risk Breakdown</div>
              <div style={{ display: "grid", gap: 12 }}>
                {v.subs.map((s) => (
                  <div key={s.key}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ font: "700 11px Manrope" }}>{s.label}</span>
                      <span style={{ font: "800 11px 'Space Grotesk'", color: s.color }}>{s.valStr}</span>
                    </div>
                    <div style={{ height: 4, borderRadius: 99, background: "rgba(255,255,255,.05)", overflow: "hidden" }}>
                      <div style={{ height: "100%", width: s.w, background: s.color }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="uplift-section">
              <div style={{ font: "800 11px Manrope", color: "#34E2A0", textTransform: "uppercase", marginBottom: 12 }}>Optimization Projection</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 20 }}>
                <span style={{ font: "700 48px 'Space Grotesk'", color: "#34E2A0", lineHeight: 1 }}>{v.afterScore}</span>
                <span style={{ font: "700 12px Manrope", color: "#8FBFAA" }}>target score <span style={{ color: "#34E2A0" }}>▲ {v.scoreUplift}</span></span>
              </div>
              <div style={{ background: "rgba(52,226,160,.06)", padding: "12px", borderRadius: "12px", border: "1px solid rgba(52,226,160,0.1)", marginBottom: 16 }}>
                <div style={{ font: "600 13px Manrope", color: "#FFF" }}>Reduce fund size from <span style={{ color: "#FF5C72" }}>{v.counts.total}</span> to <span style={{ color: "#34E2A0" }}>{v.counts.buy}</span></div>
              </div>
              <div style={{ display: "grid", gap: 8, padding: "0 4px" }}>
                {v.deltas.map((d, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ font: "500 11px Manrope", color: "#8794AB" }}>{d.label}</span>
                    <span style={{ font: "700 11px 'Space Grotesk'" }}>{d.to}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="no-print" style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 16, alignItems: "center" }}>
          <div style={{ position: "relative", flex: "1 1 240px" }}>
            <SafeIcon icon={FiSearch} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#8794AB", fontSize: 14 }} />
            <input type="text" placeholder="Search schemes..." value={searchTerm} onChange={(e)=>setSearchTerm(e.target.value)} className="filter-input" style={{ width: "100%", padding: "8px 12px 8px 34px", background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.1)", borderRadius: 8, color: "#FFF", fontSize: 12 }} />
          </div>
          <select value={catFilter} onChange={(e)=>setCatFilter(e.target.value)} className="filter-input" style={{ padding: "8px 12px", background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.1)", borderRadius: 8, color: "#FFF", fontSize: 12 }}>
            <option value="All">All Categories</option>
            {v.categories.filter(c => c !== "All").map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <div style={{ marginLeft: "auto", font: "600 11px Manrope", color: "#5E6B82" }}>Showing {v.rows.length} of {v.count}</div>
        </div>

        <div className="snap-scroll" style={{ overflowX: "auto", border: "1px solid rgba(255,255,255,.08)", borderRadius: 16, background: "rgba(255,255,255,.01)" }}>
          <div style={{ minWidth: 950 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 0.8fr 0.8fr 0.6fr 0.6fr 0.6fr 1fr 0.3fr", background: "rgba(255,255,255,.03)", borderBottom: "1px solid rgba(255,255,255,.08)" }}>
              <SortHeader label="SCHEME" sortKey="name" />
              <SortHeader label="CATEGORY" sortKey="cat" />
              <SortHeader label="VALUE" sortKey="cv" align="right" />
              <SortHeader label="ALLOC" sortKey="allocNum" align="right" />
              <SortHeader label="XIRR" sortKey="xirr" align="right" />
              <SortHeader label="1-YR" sortKey="r1" align="right" />
              <SortHeader label="EXP" sortKey="exp" align="right" />
              <SortHeader label="CALL" sortKey="verdict" align="center" />
              <div />
            </div>

            {v.rows.map((r, i) => (
              <div key={r.id} style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 0.8fr 0.8fr 0.6fr 0.6fr 0.6fr 1fr 0.3fr", alignItems: "center", borderBottom: "1px solid rgba(255,255,255,.04)", background: i % 2 ? "#0C1119" : "#0B0F17" }}>
                <div style={{ padding: "10px 16px" }}>
                  <input className="editable-cell" value={r.name} onChange={(e)=>updateFund(r.id,'name',e.target.value)} style={{ font: "700 12px Manrope" }} />
                  <span className="print-only" style={{ font: "700 12px Manrope" }}>{r.name}</span>
                </div>
                <div style={{ padding: "10px 8px" }}>
                  <input className="editable-cell" value={r.cat} onChange={(e)=>updateFund(r.id,'cat',e.target.value)} style={{ font: "600 10px Manrope", color: "#8794AB" }} />
                  <span className="print-only" style={{ font: "600 10px Manrope", color: "#8794AB" }}>{r.cat}</span>
                </div>
                <div style={{ padding: "10px 8px", textAlign: "right" }}>
                  <input className="editable-cell" value={r.cv} onChange={(e)=>updateFund(r.id,'cv',e.target.value)} style={{ textAlign: "right", font: "700 12px 'Space Grotesk'" }} />
                  <span className="print-only" style={{ font: "700 12px 'Space Grotesk'" }}>₹{inr(safeNum(r.cv))}</span>
                </div>
                <div style={{ padding: "10px 8px", textAlign: "right", font: "700 11px 'Space Grotesk'" }}>{r.allocStr}</div>
                <div style={{ padding: "10px 8px", textAlign: "right" }}>
                  <input className="editable-cell" value={r.xirr} onChange={(e)=>updateFund(r.id,'xirr',e.target.value)} style={{ textAlign: "right", font: "700 11px 'Space Grotesk'" }} />
                  <span className="print-only" style={{ font: "700 11px 'Space Grotesk'" }}>{r.xirr}%</span>
                </div>
                <div style={{ padding: "10px 8px", textAlign: "right" }}>
                  <input className="editable-cell" value={r.r1} onChange={(e)=>updateFund(r.id,'r1',e.target.value)} style={{ textAlign: "right", font: "700 11px 'Space Grotesk'", color: r.r1Color }} />
                  <span className="print-only" style={{ font: "700 11px 'Space Grotesk'", color: r.r1Color }}>{r.r1}%</span>
                </div>
                <div style={{ padding: "10px 8px", textAlign: "right" }}>
                  <input className="editable-cell" value={r.exp} onChange={(e)=>updateFund(r.id,'exp',e.target.value)} style={{ textAlign: "right", font: "700 11px 'Space Grotesk'" }} />
                  <span className="print-only" style={{ font: "700 11px 'Space Grotesk'" }}>{r.exp}%</span>
                </div>
                <div style={{ padding: "10px 8px", textAlign: "center" }}>
                  <select className="snap-sel" value={r.verdict} onChange={(e)=>setVerdict(r.id,e.target.value)} style={{ padding: "5px 8px", borderRadius: 6, font: "800 9px Manrope", color: r.vm.color, background: r.vm.bg, border: `1px solid ${r.vm.border}`, cursor: "pointer" }}>
                    <option value="buy">BUY</option><option value="hold">HOLD</option><option value="sellsoon">SELL · 1-3Y</option><option value="sell">SELL</option>
                  </select>
                  <span className="print-only" style={{ padding: "4px 8px", borderRadius: 4, font: "800 9px Manrope", color: r.vm.color, background: r.vm.bg, border: `1px solid ${r.vm.border}` }}>{r.vm.label}</span>
                </div>
                <div className="no-print" style={{ textAlign: "center" }}><button onClick={()=>removeFund(r.id)} style={{ color: "#5E6B82", fontSize: 16, background: "none", border: "none", cursor: "pointer" }}>×</button></div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 32, marginTop: 48 }}>
          <div>
            <div style={{ font: "800 11px Manrope", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}><span style={{ width: 3, height: 12, background: "#34E2A0" }}/>SEBI Research Analyst</div>
            <p style={{ font: "500 11px/1.6 Manrope", color: "#8794AB" }}>iNVESTT Research & Advisory (INH000010858). Analysis provided is for informational purposes only.</p>
          </div>
          <div>
            <div style={{ font: "800 11px Manrope", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}><span style={{ width: 3, height: 12, background: "#FF5C72" }}/>Risk Disclosure</div>
            <p style={{ font: "500 11px/1.6 Manrope", color: "#8794AB" }}>Mutual Fund investments are subject to market risks. Read all scheme related documents carefully before investing.</p>
          </div>
          <div>
            <div style={{ font: "800 11px Manrope", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}><span style={{ width: 3, height: 12, background: "#4F9CF0" }}/>Limitation</div>
            <p style={{ font: "500 11px/1.6 Manrope", color: "#8794AB" }}>Scores are generated using quantitative metrics. Consult a qualified advisor for final investment decisions.</p>
          </div>
        </div>

        <footer style={{ marginTop: 40, textAlign: "center", font: "600 10px Manrope", color: "#5E6B82", borderTop: "1px solid rgba(255,255,255,.04)", paddingTop: 20 }}>
          © {new Date().getFullYear()} iNVESTT · SEBI RA Registration: INH000010858
        </footer>
      </div>
    </div>
  );
}